'use client'

import { useEffect, useState, useCallback } from 'react'
import { useLocation, useNavigate } from '../lib/next-router-compat'
import { Avatar, Breadcrumbs, Button, Dropdown, Label, Separator } from '@heroui/react'
import { AppLayout, Navbar, Sidebar } from '@heroui-pro/react'
import { Bell, LayoutDashboard, LogOut, Menu, Search, Settings, X } from 'lucide-react'
import { IconButton } from './IconButton'
import { useAuth } from '../lib/auth'
import { APP_NAME } from '../lib/branding'
import { getRouteTitle, getBreadcrumbItems } from './navigation'
import { AppSidebarContent } from './AppSidebar'
import { CommandPalette } from './CommandPalette'

function UserInitials({ name }: { name?: string }): React.ReactElement {
  const initials = (name || 'Admin')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.slice(0, 1).toUpperCase())
    .join('')

  return <span className="text-xs font-semibold">{initials || 'A'}</span>
}

export function AppShell({ children }: { children: React.ReactNode }): React.ReactElement | null {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isCommandOpen, setCommandOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const routeTitle = getRouteTitle(location.pathname)
  const breadcrumbItems = getBreadcrumbItems(location.pathname)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted && !user) {
      navigate('/login')
    }
  }, [mounted, navigate, user])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileNavOpen) return

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMobileNavOpen(false)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [mobileNavOpen])

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null
      const isEditable =
        target?.closest('input, textarea, select, [contenteditable="true"]') !== null

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !isEditable) {
        event.preventDefault()
        setCommandOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleLogout = useCallback((): void => {
    setMobileNavOpen(false)
    logout()
    void navigate('/login')
  }, [logout, navigate])

  const go = useCallback((path: string): void => {
    setMobileNavOpen(false)
    void navigate(path)
  }, [navigate])

  if (!mounted || !user) {
    return null
  }

  function handleAccountAction(key: React.Key): void {
    if (key === 'dashboard') void navigate('/')
    if (key === 'settings') void navigate('/settings')
    if (key === 'sign-out') handleLogout()
  }

  const sidebarProps = {
    pathname: location.pathname,
    onLogout: handleLogout,
    onNavigate: go,
  }

  return (
    <>
      <AppLayout
        defaultSidebarOpen
        navigate={(href) => { void navigate(href) }}
        sidebarCollapsible="icon"
        sidebar={
          <Sidebar>
            <AppSidebarContent {...sidebarProps} />
            <Sidebar.Rail />
          </Sidebar>
        }
        navbar={
          <Navbar aria-label={`${APP_NAME} workspace`} maxWidth="full">
            <Navbar.Header>
              {/* Library MenuToggle/Trigger are not wired; this owns the mobile drawer. */}
              <button
                type="button"
                className="app-layout-menu-toggle"
                aria-expanded={mobileNavOpen}
                aria-controls="app-mobile-nav"
                aria-label="Open navigation"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu className="size-5" />
              </button>

              {/* Phone: page title (breadcrumbs are sm+ only). */}
              <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground sm:hidden">
                {routeTitle}
              </p>

              <Breadcrumbs className="hidden min-w-0 sm:flex">
                {breadcrumbItems.map((item, index) => {
                  const isLast = index === breadcrumbItems.length - 1
                  const content = (
                    <span className="flex min-w-0 items-center gap-2 overflow-hidden">
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </span>
                  )

                  if (!isLast && item.to) {
                    return (
                      <Breadcrumbs.Item
                        key={`${item.label}-${index}`}
                        className="min-w-0 font-semibold no-underline"
                        href={item.to}
                      >
                        {content}
                      </Breadcrumbs.Item>
                    )
                  }

                  return (
                    <Breadcrumbs.Item
                      key={`${item.label}-${index}`}
                      className="min-w-0 font-semibold"
                    >
                      {content}
                    </Breadcrumbs.Item>
                  )
                })}
              </Breadcrumbs>

              <Navbar.Spacer className="hidden sm:block" />

              {/* Always reachable on phone — do not gate behind min-width. */}
              <Navbar.Content className="flex shrink-0 items-center gap-0.5">
                <IconButton label="Search workspace" size="sm" variant="ghost" onPress={() => setCommandOpen(true)}>
                  <Search className="size-4" />
                </IconButton>
                <IconButton label="Notifications" size="sm" variant="ghost" onPress={() => { void navigate('/notifications') }}>
                  <Bell className="size-4" />
                </IconButton>
                <Navbar.Separator className="hidden min-[400px]:block" />
                <Dropdown>
                  <Button isIconOnly aria-label="Account menu" variant="ghost">
                    <Avatar className="size-6 bg-surface-secondary text-foreground">
                      <Avatar.Fallback>
                        <UserInitials name={user?.name} />
                      </Avatar.Fallback>
                    </Avatar>
                  </Button>
                  <Dropdown.Popover className="min-w-[220px]" placement="bottom end">
                    <Dropdown.Menu onAction={handleAccountAction}>
                      <Dropdown.Item id="dashboard" textValue="Dashboard">
                        <LayoutDashboard className="size-4 text-muted" />
                        <Label>Dashboard</Label>
                      </Dropdown.Item>
                      <Dropdown.Item id="settings" textValue="Settings">
                        <Settings className="size-4 text-muted" />
                        <Label>Settings</Label>
                      </Dropdown.Item>
                      <Separator />
                      <Dropdown.Item id="sign-out" textValue="Sign Out" variant="danger">
                        <LogOut className="size-4 text-muted" />
                        <Label>Sign Out</Label>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown>
              </Navbar.Content>
            </Navbar.Header>
          </Navbar>
        }
      >
        {children}
      </AppLayout>

      {mobileNavOpen ? (
        <div className="app-mobile-nav" id="app-mobile-nav" role="dialog" aria-modal="true" aria-label="Workspace navigation">
          <button
            type="button"
            className="app-mobile-nav__backdrop"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="sidebar app-mobile-nav__panel">
            <div className="flex items-center justify-end border-b border-border px-2 py-1.5 md:hidden">
              <IconButton label="Close navigation" size="sm" variant="ghost" onPress={() => setMobileNavOpen(false)}>
                <X className="size-4" />
              </IconButton>
            </div>
            <AppSidebarContent {...sidebarProps} />
          </aside>
        </div>
      ) : null}

      <CommandPalette
        isOpen={isCommandOpen}
        onOpenChange={setCommandOpen}
        routeTitle={routeTitle}
        onNavigate={(route) => {
          setCommandOpen(false)
          setMobileNavOpen(false)
          void navigate(route)
        }}
      />
    </>
  )
}
